/**
 * TikTok Pixel Integration
 * Waits for sales channel ttq or self-initializes.
 * Configures Advanced Matching and tracks events.
 *
 * Standalone version for co-aurora.
 * Ported from shopper/src/scripts/lib/integrations/tiktok.js
 */
(function() {
  'use strict';

  function TikTok(config) {
    this.pixelId = config.pixelId;
    window.TrackingBase.call(this, Object.assign({
      name: 'TikTok',
      enableEventsAPI: true,
      enableAdvancedMatching: true,
      enableDeduplication: true
    }, config));
  }

  TikTok.prototype = Object.create(window.TrackingBase.prototype);
  TikTok.prototype.constructor = TikTok;

  /* ---- Initialize ---- */

  TikTok.prototype.initialize = function() {
    if (!this.pixelId) {
      this.warn('TikTok Pixel ID not configured');
      this.config.enabled = false;
      return;
    }

    var self = this;
    return this.waitForTag('ttq', 'TikTok Pixel').then(function(loaded) {
      if (loaded) {
        self._completeTikTokSetup();
      } else {
        self._initializeTikTokPixel();
      }
    });
  };

  TikTok.prototype._initializeTikTokPixel = function() {
    /* Create ttq stub */
    window.ttq = window.ttq || [];
    window.ttq.methods = [
      'page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once',
      'ready', 'alias', 'group', 'enableCookie', 'disableCookie'
    ];
    window.ttq.setAndDefer = function(t, e) {
      t[e] = function() { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); };
    };
    for (var i = 0; i < window.ttq.methods.length; i++) {
      window.ttq.setAndDefer(window.ttq, window.ttq.methods[i]);
    }
    window.ttq.instance = function(t) {
      var e = window.ttq._i[t] || [];
      window.ttq.methods.forEach(function(m) { e[m] = window.ttq[m]; });
      return e;
    };
    window.ttq.load = function(e, n) {
      var src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      window.ttq._i = window.ttq._i || {};
      window.ttq._i[e] = [];
      window.ttq._i[e]._u = src;
      window.ttq._t = window.ttq._t || {};
      window.ttq._t[e] = +new Date();
      window.ttq._o = window.ttq._o || {};
      window.ttq._o[e] = n || {};
      var o = document.createElement('script');
      o.type = 'text/javascript';
      o.async = true;
      o.src = src + '?sdkid=' + e + '&lib=ttq';
      var a = document.getElementsByTagName('script')[0];
      a.parentNode.insertBefore(o, a);
    };

    window.ttq.load(this.pixelId);
    window.ttq.page();

    this.tagLoaded = true;
    this.log('TikTok Pixel initialized');
    this._completeTikTokSetup();
  };

  TikTok.prototype._completeTikTokSetup = function() {
    this._configureAdvancedMatching();

    if (window.AttributionManager) {
      var tiktokData = window.AttributionManager.getTikTokData();
      if (tiktokData.ttclid) {
        window.ttq.page({ ttclid: tiktokData.ttclid });
      }
    }
  };

  /* ---- Advanced Matching ---- */

  TikTok.prototype._configureAdvancedMatching = function() {
    if (!this.config.enableAdvancedMatching) return;

    var cd = this.getCustomerData();
    var am = {};

    if (cd.email) am.email = cd.email.toLowerCase();
    if (cd.phone) am.phone_number = this._normalizePhone(cd.phone);
    if (cd.customer_id) am.external_id = cd.customer_id.toString();
    if (cd.first_name) am.first_name = cd.first_name.toLowerCase();
    if (cd.last_name) am.last_name = cd.last_name.toLowerCase();
    if (cd.city) am.city = cd.city.toLowerCase();
    if (cd.state) am.state = cd.state.toLowerCase();
    if (cd.postal_code) am.zip = cd.postal_code;
    if (cd.country) am.country = cd.country.toLowerCase();

    /* Attribution enrichment */
    if (window.AttributionManager) {
      var attrCustomer = window.AttributionManager.getCustomerData();
      if (!am.email && attrCustomer.email) am.email = attrCustomer.email.toLowerCase();
      if (!am.phone_number && attrCustomer.phone) am.phone_number = this._normalizePhone(attrCustomer.phone);
    }

    if (Object.keys(am).length > 0 && window.ttq) {
      window.ttq.identify(am);
      this.log('Advanced Matching configured', { fields: Object.keys(am) });
    }
  };

  /* ---- Track ---- */

  TikTok.prototype.track = function(eventName, eventData, options) {
    options = options || {};
    eventData = eventData || {};

    if (!this.tagLoaded || !window.ttq) {
      this.queueEvent(eventName, eventData, options);
      return;
    }

    this._configureAdvancedMatching();

    var ttEventName = this._mapEventName(eventName);
    if (!ttEventName) {
      this.log('Event ' + eventName + ' not mapped for TikTok');
      return;
    }

    var ttData = this._formatEventData(eventName, eventData);

    if (window.AttributionManager) {
      var tiktokData = window.AttributionManager.getTikTokData();
      if (tiktokData.ttclid) ttData.ttclid = tiktokData.ttclid;
    }

    window.ttq.track(ttEventName, ttData);
    this.log('Tracked ' + ttEventName, { eventId: options.eventID });
  };

  TikTok.prototype._mapEventName = function(eventName) {
    var map = {
      'page_view': 'ViewContent',
      'view_item': 'ViewContent',
      'add_to_cart': 'AddToCart',
      'begin_checkout': 'InitiateCheckout',
      'add_payment_info': 'AddPaymentInfo',
      'purchase': 'CompletePayment',
      'search': 'Search',
      'sign_up': 'CompleteRegistration',
      'subscribe': 'Subscribe',
      'contact': 'Contact',
      'submit_form': 'SubmitForm',
      'complete_registration': 'CompleteRegistration'
    };
    return map[eventName];
  };

  TikTok.prototype._formatEventData = function(eventName, eventData) {
    var f = {};
    if (eventData.value) f.value = eventData.value;
    if (eventData.currency) f.currency = eventData.currency;
    if (eventData.search_term) f.query = eventData.search_term;

    if (eventData.items && eventData.items.length > 0) {
      f.contents = eventData.items.map(function(item) {
        return {
          content_id: item.item_id || item.product_id,
          content_name: item.item_name,
          content_category: item.item_category,
          content_type: 'product',
          price: item.price,
          quantity: item.quantity
        };
      });
      f.content_ids = f.contents.map(function(c) { return c.content_id; });
      f.content_type = 'product';
    }

    if (eventName === 'purchase') f.order_id = eventData.transaction_id;
    return f;
  };

  TikTok.prototype._normalizePhone = function(phone) {
    var n = phone.replace(/\D/g, '');
    if (n.length === 10) n = '1' + n;
    return n;
  };

  /* Export */
  window.TrackingTikTok = TikTok;
})();
